/**
 * PaywallSheet — single canonical upgrade surface for the web app.
 *
 * Web subscriptions go through hosted web checkout when billing is
 * enabled (webBilling.config); otherwise the sheet hands off to the
 * iOS + Android apps (Apple IAP / Google Play Billing via RevenueCat).
 *
 * Every premium-gated tap funnels through this ONE component so the
 * upgrade moment looks identical wherever it surfaces. Only the
 * headline + subtitle vary, keyed by `trigger` — the action the user
 * just tried to take. The supported triggers map 1:1 to entry points
 * in the product:
 *
 *   dm_open          — opened the DM inbox without premium
 *   dm_send          — tried to message someone from a public profile
 *   subscribe        — generic upgrade tap (Home pitch, etc.)
 *   pricing          — tapped Subscribe on the /pricing plan cards.
 *                       Different from `subscribe`: the user has just
 *                       seen the prices and expects to pay, so the copy
 *                       leads with why checkout isn't on the web.
 *   manage           — tapped Manage on the Settings plan badge. Different
 *                       from `subscribe`: the user is asking how to change /
 *                       cancel an existing plan (or sign up if free), not
 *                       being pitched mid-task. Copy reflects that.
 *   rating_note      — tapped a rating row to read the attached note
 *   disappearing     — tapped the disappearing-message timer in a DM
 *   bookmark_limit   — tried to add an 11th bookmark on free
 *   dm_message_limit — tried to send the 11th message in a free thread
 *   discover_limit   — hit the daily free swipe cap in the deck
 *   discover_undo    — free user's 2nd session undo in the deck
 *
 * Shape adapts to viewport:
 *   - ≤900px: bottom-sheet (slide-up, rounded top corners, full width)
 *   - >900px: centred modal capped at 480px wide
 *
 * Layout: hero → benefits list → store handoff → fine print. The
 * benefits list is the generic value-prop (every premium gate); the
 * hero stays trigger-specific. On desktop the modal can scroll within
 * its 90vh cap if needed, but typical heights still fit at once.
 *
 * Closes on scrim click + ESC.
 */
import { useEffect, useState } from 'react';
import { useAction, useQuery } from 'convex/react';
import { QRCodeSVG } from 'qrcode.react';
import { api as convexApi } from '../../../convex/functions/_generated/api';
import { APP_STORE_URL, GET_THE_APP_URL, PLAY_STORE_URL } from '@/lib/stores';
import { AppStoreBadge, PlayStoreBadge } from './StoreBadges';
import { Icons, type IconKey } from './Icons';
import type { PaywallOneTime } from '@/lib/paywall';

type WebPlan = 'weekly' | 'monthly' | 'yearly';

// Web checkout plan cards (mirror util.PLAN_PRICE_USD / mobile DISPLAY_PRICES).
// Shown only when web billing is live (config.enabled); otherwise the sheet
// falls back to the App-Store handoff below.
const WEB_PLANS: ReadonlyArray<{ plan: WebPlan; label: string; price: string; note?: string }> = [
  { plan: 'yearly', label: 'Yearly', price: '$279.99/yr', note: 'Save 22%' },
  { plan: 'monthly', label: 'Monthly', price: '$29.99/mo' },
  { plan: 'weekly', label: 'Weekly', price: '$9.99/wk' },
];

export type PaywallTrigger =
  | 'dm_open'
  | 'dm_send'
  | 'subscribe'
  | 'pricing'
  | 'manage'
  | 'rating_note'
  | 'disappearing'
  | 'bookmark_limit'
  | 'dm_message_limit'
  | 'discover_limit'
  | 'discover_undo'
  // The agent/developer platform (mint a key, connect an agent)
  // without Ultra. Distinct from the premium triggers: this sheet sells
  // the Ultra plan, not Premium.
  | 'agent_platform'
  // IAP one-time consumable triggers (dual-offer). Web can't purchase,
  // so these surface the price + route to Get-the-app.
  | 'comment_boost'
  | 'profile_boost'
  | 'reveal_rater'
  | 'unlimited_thread';

interface Props {
  open: boolean;
  trigger: PaywallTrigger;
  onClose: () => void;
  // IAP dual-offer hint (mirror of mobile). When set or derivable from
  // the trigger, the sheet shows a one-time price card above the
  // benefits; the conversion path stays Get-the-app (no web IAP).
  oneTime?: PaywallOneTime;
}

// One-time consumable copy (web mirror of mobile CONSUMABLE_DISPLAY,
// minus the store productId — web never calls the store SDK).
const CONSUMABLE_DISPLAY: Record<string, { price: string; title: string; sub: string }> = {
  dm_pass: { price: '$0.99', title: 'Send this one DM', sub: 'A one-time pass to start this conversation.' },
  unlimited_thread: { price: '$1.49', title: 'Unlock this chat', sub: 'Unlimited messages in this one conversation.' },
  comment_boost: { price: '$1.49', title: 'Boost your comment', sub: 'Pin your comment to the top of their list for 48 hours.' },
  keeper_spot: { price: '$0.99', title: 'Add one more keeper', sub: 'One extra keeper spot beyond your free 10.' },
  profile_boost: { price: '$1.49', title: 'Boost your profile', sub: 'Jump to the top of nearby decks for a few hours.' },
  extra_swipes: { price: '$0.99', title: 'Get 25 more swipes', sub: "Keep going past today's free limit." },
  reveal_rater: { price: '£4.99', title: 'Reveal this rater', sub: 'See who left this anonymous rating.' },
  undo_swipe: { price: '$0.99', title: 'Undo this swipe', sub: 'Bring the last profile back.' },
};

const TRIGGER_TO_CONSUMABLE: Partial<Record<PaywallTrigger, string>> = {
  dm_send: 'dm_pass',
  dm_message_limit: 'unlimited_thread',
  bookmark_limit: 'keeper_spot',
  discover_limit: 'extra_swipes',
  discover_undo: 'undo_swipe',
  comment_boost: 'comment_boost',
  profile_boost: 'profile_boost',
  reveal_rater: 'reveal_rater',
  unlimited_thread: 'unlimited_thread',
};

// Generic premium value-prop list shown below the trigger-specific
// hero. Covers every premium gate — every
// reason a user might want to upgrade — so the sheet sells the whole
// product, not just the action that triggered it. Ordered by
// conversion strength: messaging + rating-note gates convert
// strongest on dating apps, then read receipts + disappearing, then
// the lower-frequency caps. RN twin (src/app/components/PaywallSheet)
// holds the same list keyed to emoji glyphs — keep both in sync when
// adding/removing entries.
const BENEFITS: ReadonlyArray<readonly [IconKey, string, string]> = [
  ['chat',     'Send unlimited messages',     'Keep every conversation going past 10 messages'],
  ['lock',     'Read every inbound message',  'Unlock messages hidden behind the free-tier cap'],
  ['star',     "See who's into you",           'Read the personal notes left on your ratings'],
  ['eye',      'Know when messages are read', 'Read receipts on every message you send'],
  ['clock',    'Send disappearing messages',  'Set messages to vanish in 5 min, 1h, 24h, or 7d'],
  ['bookmark', 'Keep unlimited profiles',     'No more 10-keeper cap on profiles you save'],
  ['send',     'Start unlimited DM threads',  "Initiate conversations with anyone you've rated"],
  ['undo',     'Undo any swipe',              'Rewind as many swipes as you like — free is capped at one'],
  ['compass',  "See who's nearby on the map", 'Open the live discover map to find people around you now'],
];

// Trigger-keyed headline. Italics live inside <em> spans so the
// serif display font can mix roman + italic the way the type system
// expects. Tone scales the verb to the action: "Start", "Reply",
// "Generate", "Unlock" — each is what the user was literally just
// trying to do.
function headline(trigger: PaywallTrigger): React.ReactNode {
  switch (trigger) {
    case 'dm_open':          return (<><em>Read</em> your messages.</>);
    case 'dm_send':          return (<><em>Start</em> the conversation.</>);
    case 'pricing':          return (<><em>Subscribe</em> in the app.</>);
    case 'manage':           return (<><em>Manage</em> in the app.</>);
    case 'rating_note':      return (<><em>See</em> what they said.</>);
    case 'disappearing':     return (<><em>Send</em> messages that vanish.</>);
    case 'bookmark_limit':   return (<><em>Save</em> every profile.</>);
    case 'dm_message_limit': return (<><em>Keep</em> the conversation going.</>);
    case 'discover_limit': return (<><em>Keep</em> swiping.</>);
    case 'discover_undo': return (<><em>Undo</em> any swipe.</>);
    case 'agent_platform': return (<><em>Run</em> your profile with an agent.</>);
    case 'unlimited_thread': return (<><em>Keep</em> the conversation going.</>);
    case 'comment_boost':    return (<><em>Boost</em> your comment.</>);
    case 'profile_boost':    return (<><em>Boost</em> your profile.</>);
    case 'reveal_rater':     return (<><em>Reveal</em> this rater.</>);
    case 'subscribe':
    default:                 return (<>Get <em>WYDM Premium.</em></>);
  }
}

// Trigger-keyed subtitle. Single sentence — the modal is one-glance.
// Each line names ONE benefit specific to the action the user took:
// reading DMs, sending DMs, AI reviews, or the whole bundle.
function subtitle(trigger: PaywallTrigger): string {
  switch (trigger) {
    case 'dm_open':
      return "See who's reached out, what they said, and reply — all premium-only.";
    case 'dm_send':
      return 'DMs are the only way to start a conversation on WYDM.';
    case 'pricing':
      return "We're still sorting out payment processing on the web app. For now, download the app from the App Store or Play Store and subscribe there.";
    case 'manage':
      return 'Subscriptions live in the App Store and Play Store. Open WYDM on your phone to switch plan, update billing, or cancel.';
    case 'rating_note':
      return "Premium members can read the notes attached to ratings. Find out who's really into you.";
    case 'disappearing':
      return 'Set messages to vanish after 5 minutes, 1 hour, 24 hours, or 7 days. Premium only.';
    case 'bookmark_limit':
      return 'Free accounts can keep up to 10 profiles. Go premium to keep them all.';
    case 'dm_message_limit':
      return "You've sent your 10 free messages in this conversation. Upgrade to send unlimited messages.";
    case 'discover_limit':
      return "You've hit today's free swipe limit. Go premium for unlimited swipes — and to undo a swipe.";
    case 'discover_undo':
      return 'Premium gives you unlimited undos.';
    case 'agent_platform':
      return 'Ultra adds the agent platform: mint API keys, connect an agent over MCP, and get webhook events for every rating, match and DM. Subscriptions live in the app.';
    case 'unlimited_thread':
      return 'Unlock unlimited messages in this conversation, or go Premium for every chat.';
    case 'comment_boost':
      return 'Pin your comment to the top of their list for 48 hours.';
    case 'profile_boost':
      return 'Jump to the top of nearby decks and get seen first.';
    case 'reveal_rater':
      return 'See who left this anonymous rating.';
    case 'subscribe':
    default:
      return 'Full DMs, every rating in detail, and more. One subscription.';
  }
}

export function PaywallSheet({ open, trigger, onClose, oneTime }: Props) {
  // IAP dual-offer (web): show the one-time price as an info card; the
  // actual purchase only exists in the app, so Get-the-app is the path.
  const oneTimeAction = oneTime?.action ?? TRIGGER_TO_CONSUMABLE[trigger] ?? null;
  const oneTimeDisp = oneTimeAction ? CONSUMABLE_DISPLAY[oneTimeAction] : null;

  // Web billing gate. `config.enabled` is false until a processor's secrets are
  // set on the deployment (see lib/webBilling.isEnabled) — so pre-launch the
  // sheet keeps its App-Store handoff and nothing here can transact.
  const webConfig = useQuery(convexApi.webBilling.config, {});
  const webEnabled = webConfig?.enabled ?? false;
  const createCheckout = useAction(convexApi.webBilling.createCheckout);
  const [checkoutBusy, setCheckoutBusy] = useState<string | null>(null);

  const beginCheckout = async (
    args:
      | { kind: 'subscription'; plan: WebPlan }
      | { kind: 'consumable'; action: string },
    key: string,
  ) => {
    if (checkoutBusy) return;
    setCheckoutBusy(key);
    try {
      const { url } = await createCheckout(args);
      // Hosted checkout — entitlement is granted server-side by the webhook;
      // the return redirect lands on /upgrade/success where the reactive `me`
      // query flips to premium with no polling.
      window.location.assign(url);
    } catch {
      // The convexDispatch interceptor surfaces the error; just clear busy.
      setCheckoutBusy(null);
    }
  };

  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 901px)').matches,
  );

  // Track viewport breakpoint so the same component renders a centred
  // modal (desktop) vs bottom-sheet (mobile) without re-mounting.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 901px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ESC closes. Re-add per open-toggle so we don't leave handlers
  // attached after unmount.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Body scroll-lock while the sheet is open so the underlying page
  // doesn't scroll behind the scrim.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(43,29,20,0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: isDesktop ? 'center' : 'flex-end',
        justifyContent: 'center',
        animation: 'fade-in 0.25s ease',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: isDesktop ? 480 : undefined,
          background: 'var(--bg)',
          borderRadius: isDesktop ? 28 : '32px 32px 0 0',
          // Tight desktop padding. With the 8-row benefits list the
          // modal may now exceed one viewport on small windows — the
          // 90vh maxHeight + overflowY:auto below catches that.
          padding: isDesktop ? '28px 28px 22px' : '14px 22px 24px',
          animation: isDesktop
            ? 'fade-in 0.25s ease'
            : 'slide-up 0.35s cubic-bezier(.2,.9,.3,1.1)',
          // Mobile keeps overflow auto for short viewports; desktop
          // is sized to fit so scrolling shouldn't trigger. The cap
          // is a safety net for very small windows or huge font
          // overrides, not the expected layout.
          maxHeight: isDesktop ? '90vh' : '90vh',
          overflowY: 'auto',
          boxShadow: isDesktop ? 'var(--shadow-card)' : undefined,
        }}
      >
        {!isDesktop && (
          <div
            aria-hidden
            style={{
              width: 40, height: 4,
              background: 'var(--line-strong)',
              borderRadius: 4,
              margin: '0 auto 16px',
            }}
          />
        )}

        {/* Hero — eyebrow, action-specific headline, single-sentence
            subtitle. No surface chrome; the type IS the design here. */}
        <div>
          <div
            className="mono"
            style={{
              fontSize: 11, color: 'var(--coral)',
              letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10,
            }}
          >
            WYDM Premium
          </div>
          <div
            className="serif"
            style={{
              fontSize: isDesktop ? 36 : 34,
              fontWeight: 500,
              lineHeight: 1.05,
              letterSpacing: -0.6,
              textWrap: 'balance' as React.CSSProperties['textWrap'],
            }}
          >
            {headline(trigger)}
          </div>
          <div
            style={{
              fontSize: 14.5,
              color: 'var(--ink-2)',
              marginTop: 12,
              lineHeight: 1.5,
              textWrap: 'balance' as React.CSSProperties['textWrap'],
            }}
          >
            {subtitle(trigger)}
          </div>
        </div>

        {/* IAP dual-offer card — the cheaper one-time option for this
            action. Web can't transact, so it's informational: the price
            + a nudge that one-time unlocks happen in the app (the
            Get-the-app section below is the actual path). */}
        {oneTimeDisp && (
          <div
            style={{
              marginTop: 18,
              background: 'var(--ink)',
              borderRadius: 16,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--bg)' }}>
                {oneTimeDisp.title}
              </div>
              <div style={{ fontSize: 12, color: 'var(--bg)', opacity: 0.82, marginTop: 2, lineHeight: 1.4 }}>
                {oneTimeDisp.sub}
                {!webEnabled && (
                  <span style={{ opacity: 0.7 }}> Buy it once in the app.</span>
                )}
              </div>
            </div>
            {webEnabled && oneTimeAction ? (
              <button
                type="button"
                onClick={() =>
                  beginCheckout(
                    { kind: 'consumable', action: oneTimeAction },
                    `one:${oneTimeAction}`,
                  )
                }
                disabled={checkoutBusy != null}
                style={{
                  flexShrink: 0,
                  border: 'none',
                  borderRadius: 999,
                  padding: '9px 16px',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: checkoutBusy ? 'default' : 'pointer',
                  background: 'var(--bg)',
                  color: 'var(--ink)',
                  opacity: checkoutBusy && checkoutBusy !== `one:${oneTimeAction}` ? 0.5 : 1,
                }}
              >
                {checkoutBusy === `one:${oneTimeAction}` ? '…' : oneTimeDisp.price}
              </button>
            ) : (
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--bg)', flexShrink: 0 }}>
                {oneTimeDisp.price}
              </div>
            )}
          </div>
        )}

        {/* Generic value-prop list — every premium gate, ordered by
            conversion strength. Trigger-specific copy in the hero
            above; this block sells the whole bundle. */}
        <BenefitsList />

        {/* Conversion path. When web billing is live, subscribe directly via
            the hosted checkout; otherwise fall back to the App-Store handoff
            (mobile stacks the badges; desktop shows a QR). */}
        {webEnabled ? (
          <WebCheckoutSection
            busyKey={checkoutBusy}
            onSubscribe={(plan) =>
              beginCheckout({ kind: 'subscription', plan }, `sub:${plan}`)
            }
          />
        ) : (
          <GetTheAppSection isDesktop={isDesktop} />
        )}

        {/* Fine print — kept tiny + dim so it doesn't compete with
            the hero. Same line on every trigger; trigger-agnostic. */}
        <div
          style={{
            textAlign: 'center', fontSize: 11,
            color: 'var(--ink-3)', marginTop: 14, lineHeight: 1.5,
          }}
        >
          {webEnabled
            ? 'Secure checkout. Cancel anytime from Settings.'
            : 'Subscriptions are managed in the App Store or Play Store.'}
          <div style={{ marginTop: 3 }}>
            <a href="/legal/terms" style={footerLink}>Terms</a>
            {' · '}
            <a href="/legal/privacy" style={footerLink}>Privacy</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// BenefitsList — compact "what you get" panel. Same surface treatment
// as other inline cards on the sheet (surface-coloured background,
// rounded, 1px line border) and a tight 10/14 row so all 8 entries
// fit alongside the store handoff without pushing the QR off-screen
// on a small desktop window. Per-row icon in a coral pill matches
// the RN twin's visual idiom.
function BenefitsList() {
  return (
    <div
      style={{
        marginTop: 18,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 16,
        padding: '10px 14px',
      }}
    >
      {BENEFITS.map(([iconKey, title, body], i) => {
        const Icon = Icons[iconKey];
        return (
          <div
            key={title}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '8px 0',
              borderTop: i > 0 ? '1px solid var(--line)' : 'none',
            }}
          >
            <div
              aria-hidden
              style={{
                width: 26, height: 26, borderRadius: 26,
                background: 'var(--coral-soft)',
                color: 'var(--coral-ink)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon size={14} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                {title}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1, lineHeight: 1.4 }}>
                {body}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// WebCheckoutSection — the web conversion surface when billing is live.
// Three plan buttons → hosted checkout. Ordered yearly-first (best value)
// to anchor high, matching the mobile paywall's plan ordering.
function WebCheckoutSection({
  busyKey,
  onSubscribe,
}: {
  busyKey: string | null;
  onSubscribe: (plan: WebPlan) => void;
}) {
  return (
    <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {WEB_PLANS.map(({ plan, label, price, note }) => {
        const key = `sub:${plan}`;
        const busy = busyKey === key;
        const dim = busyKey != null && !busy;
        return (
          <button
            key={plan}
            type="button"
            onClick={() => onSubscribe(plan)}
            disabled={busyKey != null}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              width: '100%',
              border: '1px solid var(--line)',
              borderRadius: 14,
              padding: '13px 16px',
              cursor: busyKey ? 'default' : 'pointer',
              background: plan === 'yearly' ? 'var(--coral)' : 'var(--surface)',
              color: plan === 'yearly' ? 'var(--coral-ink)' : 'var(--ink)',
              opacity: dim ? 0.5 : 1,
              transition: 'opacity .15s ease',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>{label}</span>
              {note && (
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    opacity: 0.85,
                  }}
                >
                  {note}
                </span>
              )}
            </span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {busy ? '…' : price}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// GetTheAppSection — the conversion surface. On mobile it shows the
// two store badges stacked (since the user already has a phone in
// hand, scanning a QR of their own screen is silly). On desktop the
// badges sit next to a QR code so the user can scan it with their
// phone and skip the "type wydm.app/get into your phone browser"
// step. SVG badges live in StoreBadges.tsx so they stay in lockstep
// with the /get redirect page's version.
function GetTheAppSection({ isDesktop }: { isDesktop: boolean }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: isDesktop ? 'row' : 'column',
        }}
      >
        {/* Store badges — stacked column on mobile (full width up to a
            sensible max), narrower side column on desktop next to the
            QR. */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          alignItems: 'stretch',
          width: isDesktop ? 148 : '100%',
          maxWidth: 240,
        }}>
          <AppStoreBadge href={APP_STORE_URL} height={44} />
          <PlayStoreBadge href={PLAY_STORE_URL} height={44} />
        </div>

        {/* QR — desktop only. Mobile users would be scanning their own
            screen with their own phone, which doesn't work. */}
        {isDesktop && (
          <div
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 4,
              padding: '8px 8px 6px',
              background: 'var(--surface)',
              borderRadius: 14,
              border: '1px solid var(--line)',
            }}
          >
            <QRCodeSVG
              value={GET_THE_APP_URL}
              size={96}
              bgColor="transparent"
              fgColor="var(--ink)"
              level="M"
            />
            <div
              className="mono"
              style={{
                fontSize: 9, color: 'var(--ink-3)',
                letterSpacing: 1, textTransform: 'uppercase',
              }}
            >
              Scan with phone
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const footerLink: React.CSSProperties = {
  color: 'var(--ink-3)',
  textDecoration: 'underline',
};
